import { Controller } from "@hotwired/stimulus"
import { get, patch } from "@rails/request.js"

/**
 * Desktop Hotwire calendar page orchestration (CONEK-4236 / CONEK-4240).
 *
 * Wraps the `/calendar` desktop body (topbar + grid + modal frames) and:
 *
 *   1. Keyboard shortcuts — m/w/d (view), t (today), ←/→ (prev/next period),
 *      Esc (clear a leftover modal frame; an open dialog handles its own
 *      Esc). View + date navigation is delegated to the topbar's
 *      already-rendered nav links (by data-testid) so the date-stepping math
 *      lives in one place (the server-side CalendarTopbarComponent). Day view,
 *      which the topbar switcher does not expose, is navigated directly.
 *
 *   2. Drag persistence — listens for the `ui-calendar:event-changed`
 *      CustomEvent emitted by `ui--calendar--editable-grid` and turns it into a
 *      booking write:
 *        * move / resize → PATCH /bookings/:id (Turbo Stream replaces the chip)
 *        * create        → open the new-booking modal prefilled with the
 *          dragged-out times (Turbo Frame "booking_modal")
 *
 * Markup contract (rendered by calendars/show.html.erb):
 *
 *   <div data-controller="pipeline--calendar"
 *        data-testid="calendar-container"
 *        data-pipeline--calendar-bookings-url-value="/s:.../bookings"
 *        data-pipeline--calendar-view-value="workweek"
 *        data-pipeline--calendar-date-value="2026-06-01">
 *     <!-- topbar with [data-testid='calendar-{today,prev,next}'] + view radios -->
 *     <!-- grid (ui--calendar--editable-grid) -->
 *     <turbo-frame id="booking_modal"></turbo-frame>
 *   </div>
 */
export default class extends Controller {
  static targets = ["grid"]
  static values = {
    // initialView + initialDate are rendered by calendars/show.html.erb and
    // asserted by CalendarsControllerTest; keep the names stable. bookingsUrl
    // is for drag-persistence writes.
    bookingsUrl: String,
    initialView: { type: String, default: "workweek" },
    initialDate: String,
  }

  connect() {
    this._onKeyDown = this._onKeyDown.bind(this)
    this._onEventChanged = this._onEventChanged.bind(this)
    this._onReload = this._onReload.bind(this)
    document.addEventListener("keydown", this._onKeyDown)
    document.addEventListener("calendar:reload", this._onReload)
    this.element.addEventListener(
      "ui-calendar:event-changed",
      this._onEventChanged,
    )
    this._dropEditBookingParam()
    this._scrollToWorkingHours()
  }

  // `?edit_booking=<id>` is one-shot: the task page's linked-event pencil opens
  // the calendar this way (CONEK-7690) and the server already rendered the
  // booking_modal frame `src`. Drop it from the address bar so view/date
  // navigation (_buildUrl) and a refresh don't reopen the edit modal.
  _dropEditBookingParam() {
    const url = new URL(window.location.href)
    if (!url.searchParams.has("edit_booking")) return
    url.searchParams.delete("edit_booking")
    window.history.replaceState(window.history.state, "", url.toString())
  }

  // ── Initial scroll (CONEK-7690, React calendar parity) ─────────────────
  //
  // The day/week grids paint all 24 hours, so the first paint opened at 00:00
  // and the working day sat below the fold. Like the React calendar, open on the
  // current time when the grid shows today (centre the now-line), else at 08:00.
  // Month view has neither anchor, so this is a no-op there. Runs on connect
  // only: a live reload (reloadGrid) keeps the user's own scroll position.
  _scrollToWorkingHours() {
    if (!this.hasGridTarget) return
    const grid = this.gridTarget
    const nowLine = grid.querySelector(
      "[data-now-line], [data-ui--calendar--now-line-tick-target='line']",
    )
    const anchor = nowLine || grid.querySelector('[data-hour="8"]')
    if (!anchor) return

    // The grids' sticky block (day headers + all-day row, conek_ui): keep 08:00
    // below all of it. [data-week-header] is the pre-0.3.17 week header.
    const header = grid.querySelector(
      "[data-grid-sticky-header], [data-week-header]",
    )
    const headerHeight = header ? header.getBoundingClientRect().height : 0
    const top =
      anchor.getBoundingClientRect().top -
      grid.getBoundingClientRect().top +
      grid.scrollTop
    grid.scrollTop = nowLine
      ? Math.max(0, top - grid.clientHeight / 2)
      : Math.max(0, top - headerHeight)
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKeyDown)
    document.removeEventListener("calendar:reload", this._onReload)
    this.element.removeEventListener(
      "ui-calendar:event-changed",
      this._onEventChanged,
    )
  }

  // ── Live grid refresh ───────────────────────────────────────────────────
  //
  // Fired by the `calendar_reload` Turbo Stream action (application.js) after any
  // booking mutation — the controller's own create/edit/drag/delete responses AND
  // MS Graph webhook echoes. Each event is an absolutely-positioned card whose
  // top/height is computed server-side, so we can't position a single injected
  // chip on the client; instead we re-fetch the current view (URL already encodes
  // view + date) and swap the grid so the server re-renders every card in place.
  // A stray chip appended to #calendar_events (the pre-CONEK-4230 behaviour) is
  // what made a just-created event land unstyled at the bottom until a refresh.
  _onReload() {
    this.reloadGrid()
  }

  async reloadGrid() {
    if (!this.hasGridTarget) return
    // Sequence guard: several reload triggers can overlap (own mutation +
    // webhook echo + another user's broadcast on a shared booking). Two
    // independent GETs have no in-order-resolution guarantee, so tag each and
    // apply only the latest — otherwise a slow earlier response could clobber a
    // fresher grid.
    const requestId = (this._reloadSeq = (this._reloadSeq || 0) + 1)
    const grid = this.gridTarget
    const scrollTop = grid.scrollTop
    let html
    try {
      const response = await get(window.location.href, { responseKind: "html" })
      if (!response.ok) return
      html = await response.html
    } catch {
      return // network hiccup — leave the grid as-is; a manual refresh recovers
    }
    if (requestId !== this._reloadSeq) return // superseded by a newer reload
    const fresh = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("calendar_events")
    if (!fresh) return
    grid.innerHTML = fresh.innerHTML
    grid.scrollTop = scrollTop // preserve the user's scroll position
  }

  // ── Keyboard shortcuts ──────────────────────────────────────────────────

  _onKeyDown(event) {
    if (event.metaKey || event.ctrlKey || event.altKey) return
    if (
      event.target &&
      typeof event.target.matches === "function" &&
      event.target.matches("input, textarea, select, [contenteditable]")
    ) {
      return
    }
    if (this._modalOwnsKeyboard(event)) return

    switch (event.key) {
      case "m":
        this._switchView("month")
        event.preventDefault()
        break
      case "w":
        this._switchView("workweek")
        event.preventDefault()
        break
      case "d":
        this._navigate(this._buildUrl({ view: "day" }))
        event.preventDefault()
        break
      case "t":
        this._clickNav("calendar-today")
        event.preventDefault()
        break
      case "ArrowLeft":
        this._clickNav("calendar-prev")
        event.preventDefault()
        break
      case "ArrowRight":
        this._clickNav("calendar-next")
        event.preventDefault()
        break
      case "Escape":
        this._closeModal()
        break
    }
  }

  // While a dialog is open in #booking_modal (booking form, detail or delete
  // modal — all conek-ui `ui--modal`s with their own Escape handling), or the
  // key comes from inside any open dialog, the dialog owns the keyboard: no
  // view/date shortcut fires behind it, and Escape is left to the modal's
  // close(), whose cancelable `modal:before-close` lets the unsaved-changes
  // guard veto it. Emptying the frame here on Escape bypassed that veto and
  // threw away a dirty form (CONEK-7677).
  _modalOwnsKeyboard(event) {
    if (event.target?.closest?.("dialog[open]")) return true
    const frame = document.getElementById("booking_modal")
    return Boolean(frame?.querySelector("dialog[open]"))
  }

  // Click the topbar view radio if present (keeps the active-state + href in
  // one place); otherwise fall back to a constructed URL.
  _switchView(view) {
    const radio = this.element.querySelector(
      `[data-testid='view-switcher'] [data-view-value='${view}']`,
    )
    if (radio) {
      radio.click()
    } else {
      this._navigate(this._buildUrl({ view }))
    }
  }

  _clickNav(testid) {
    const link = this.element.querySelector(`[data-testid='${testid}']`)
    if (link) {
      link.click()
    } else if (testid === "calendar-today") {
      this._navigate(
        this._buildUrl({ date: new Date().toISOString().slice(0, 10) }),
      )
    }
  }

  // Topbar date picker (CONEK-7690): jump to the picked day, keeping the view.
  jumpToDate(event) {
    const picked = event.detail?.dates?.[0]
    if (!(picked instanceof Date) || Number.isNaN(picked.getTime())) return
    const pad = (n) => String(n).padStart(2, "0")
    const date = `${picked.getFullYear()}-${pad(picked.getMonth() + 1)}-${pad(picked.getDate())}`
    this._navigate(this._buildUrl({ date }))
  }

  _buildUrl({ view, date } = {}) {
    const url = new URL(window.location.href)
    url.searchParams.set("view", view || this.initialViewValue)
    url.searchParams.set("date", date || this.initialDateValue)
    return url.toString()
  }

  _navigate(href) {
    window.location.href = href
  }

  _closeModal() {
    const frame = document.getElementById("booking_modal")
    if (frame) frame.replaceChildren()
  }

  // ── Drag persistence ────────────────────────────────────────────────────

  async _onEventChanged(event) {
    const detail = event.detail || {}
    if (detail.action === "create") {
      this._openCreateModal(detail)
      return
    }
    if (!detail.id) return

    // Only start_at/end_at go up: extra detail keys (an all-day bar's
    // `all_day`, CONEK-8153) must never reach the body — BookingsController's
    // drag_payload? accepts a booking holding only start_time/end_time.
    await patch(`${this.bookingsUrlValue}/${detail.id}`, {
      body: JSON.stringify({
        booking: { start_time: detail.start_at, end_time: detail.end_at },
      }),
      contentType: "application/json",
      responseKind: "turbo-stream",
    })
  }

  // Open the new-booking modal (Turbo Frame) prefilled with the dragged times.
  _openCreateModal(detail) {
    const url = new URL(`${this.bookingsUrlValue}/new`, window.location.origin)
    if (detail.start_at) url.searchParams.set("start_time", detail.start_at)
    if (detail.end_at) url.searchParams.set("end_time", detail.end_at)
    const frame = document.getElementById("booking_modal")
    if (frame && "src" in frame) {
      frame.src = url.toString()
    } else {
      window.location.href = url.toString()
    }
  }
}
